/**
 * i18n Configuration
 * 
 * Initializes i18next for internationalization with:
 * - Support for English (default) and German
 * - Integration with the existing language selector
 * - Automatic language detection from localStorage
 *
 * The interface is translated into fewer languages than the search index can
 * analyze. The stored preference is a *content* language, so it is mapped down
 * to a language we actually ship a bundle for; picking Italian gives Italian
 * search results with an English interface.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { normalizeUiLanguage, DEFAULT_LANGUAGE } from './languages';

// Import translation files
import enCommon from './locales/en/common.json';
import enSearch from './locales/en/search.json';
import enFeedback from './locales/en/feedback.json';
import enAdmin from './locales/en/admin.json';
import deCommon from './locales/de/common.json';
import deSearch from './locales/de/search.json';
import deFeedback from './locales/de/feedback.json';
import deAdmin from './locales/de/admin.json';

// Storage key used by the existing LanguageSelector
const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

/**
 * Get the initial UI language from localStorage or browser settings
 */
function getInitialLanguage(): string {
  // Check localStorage first (from existing LanguageSelector)
  const stored = normalizeUiLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  if (stored) {
    return stored;
  }

  // Try to match browser language
  const browserLang = normalizeUiLanguage(navigator.language);
  if (browserLang) {
    return browserLang;
  }

  // Default to English
  return DEFAULT_LANGUAGE;
}

// Initialize i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        search: enSearch,
        feedback: enFeedback,
        admin: enAdmin,
      },
      de: {
        common: deCommon,
        search: deSearch,
        feedback: deFeedback,
        admin: deAdmin,
      },
    },
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'search', 'feedback', 'admin'],
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: false, // Disable suspense for simpler integration
    },
  });

// Listen for language changes from the LanguageSelector
window.addEventListener('languageChange', (event: Event) => {
  const customEvent = event as CustomEvent<string>;
  // The event carries a content language, which may have no bundle. Map it down
  // so i18n.language is always something we can actually render.
  const newLanguage = normalizeUiLanguage(customEvent.detail) ?? DEFAULT_LANGUAGE;
  if (i18n.language !== newLanguage) {
    i18n.changeLanguage(newLanguage);
  }
});

export default i18n;
